import React from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { emptySegment, segmentKeyFromName, starterSegments, upfrontBenefits } from "./draftModel";
import { CheckField, Messages, NumberField, Section, TextField } from "./fields";

export const SEGMENTS_HELP =
  "Bikes are matched by their website model. A bike whose model is not in any segment uses the default segment. Product-group overrides above still win over segments.";
export const SEGMENT_MARGIN_HELP =
  "With segments on, the dealer margin always comes from the bike's segment, even where a product-group override sets the price.";
export const NO_MODELS_TEXT = "No website models came from the service, so models cannot be picked here yet.";
export const NOT_A_MODEL_TEXT = "not a website model";

// Same look as the product group picker; the menu is fixed-position so the editor does not clip it.
const selectStyles = {
  option: (provided) => ({ ...provided, fontSize: "13px" }),
  control: (provided, state) => ({
    ...provided,
    fontSize: "13px",
    borderColor: state.selectProps["aria-invalid"] ? "#F84646" : provided.borderColor,
  }),
};

// The website models of one segment. A model another segment already has is shown, disabled, with
// that segment's name. A stored slug the list does not have is kept and marked.
const ModelsField = ({ id, value, onChange, models, ownerOf, messages, disabled, loading }) => {
  const options = models.map((model) => ({
    value: model.slug,
    label: model.name,
    takenBy: ownerOf(model.slug),
  }));
  const selected = value.map(
    (slug) => options.find((option) => option.value === slug) || { value: slug, label: slug, unknown: !loading }
  );
  const hasError = (messages || []).some((item) => item.severity === "error");
  const hasMessages = messages && messages.length > 0;
  return (
    <Form.Group className="mb-3">
      <Form.Label htmlFor={id}>Models</Form.Label>
      <div className="selectMultiselect">
        <Select
          inputId={id}
          isMulti
          value={selected}
          options={options}
          onChange={(chosen) => onChange((chosen || []).map((option) => option.value))}
          isLoading={loading}
          isDisabled={disabled}
          isOptionDisabled={(option) => Boolean(option.takenBy)}
          formatOptionLabel={(option, meta) => {
            if (option.unknown) {
              return (
                <span>
                  {option.label} <em className="warranty-help-inline">({NOT_A_MODEL_TEXT})</em>
                </span>
              );
            }
            if (option.takenBy && meta.context === "menu") {
              return (
                <span>
                  {option.label} <em className="warranty-help-inline">(in {option.takenBy})</em>
                </span>
              );
            }
            return option.label;
          }}
          className="godown-select-container"
          classNamePrefix="godown-select"
          styles={selectStyles}
          placeholder={loading ? "Loading models" : "Choose models"}
          noOptionsMessage={() => "No model matches"}
          menuPlacement="auto"
          menuPosition="fixed"
          aria-invalid={hasError}
          aria-errormessage={hasMessages ? `${id}-msg` : undefined}
        />
      </div>
      {!loading && models.length === 0 && <div className="warranty-help">{NO_MODELS_TEXT}</div>}
      <Messages id={`${id}-msg`} messages={messages} />
    </Form.Group>
  );
};

// `models` is the website model list ({ slug, name }) from the product groups answer, empty when
// the service sent none; `modelsLoading` is true until that answer arrives.
const SegmentsSection = ({ form, setForm, msgs, locked, models = [], modelsLoading = false }) => {
  const rule = form.pricingRule;
  const segments = rule.segments;
  const upfront = upfrontBenefits(form);
  const tenures = form.tenures.filter((tenure) => tenure !== "");

  const setSegments = (update) =>
    setForm((prev) => ({
      ...prev,
      pricingRule: { ...prev.pricingRule, segments: update(prev.pricingRule.segments) },
    }));
  const patch = (index, changes) =>
    setSegments((list) => list.map((segment, i) => (i === index ? { ...segment, ...changes } : segment)));
  const patchMap = (index, mapName, key, value) =>
    setSegments((list) =>
      list.map((segment, i) =>
        i === index ? { ...segment, [mapName]: { ...segment[mapName], [key]: value } } : segment
      )
    );
  // A new segment's key follows its name until the key is typed; a stored key never changes.
  const setName = (index, name) =>
    setSegments((list) =>
      list.map((segment, i) => {
        if (i !== index) {
          return segment;
        }
        return segment.isNew && !segment.keyEdited
          ? { ...segment, name, key: segmentKeyFromName(name) }
          : { ...segment, name };
      })
    );
  const setDefault = (index) =>
    setSegments((list) => list.map((segment, i) => ({ ...segment, isDefault: i === index })));
  const setEnabled = (checked) =>
    setForm((prev) => ({
      ...prev,
      pricingRule: {
        ...prev.pricingRule,
        segmentsEnabled: checked,
        segmentsSent: prev.pricingRule.segmentsSent || checked,
      },
    }));

  const sectionMessages = [
    ...msgs("pricingRule.segments"),
    ...(rule.segmentsEnabled ? [...msgs("pricingRule.segments.items"), ...msgs("pricingRule.segments.defaultKey")] : []),
  ];

  return (
    <Section id="segments" title="Bike segments" description={SEGMENTS_HELP} messages={sectionMessages}>
      <CheckField
        id="warranty-segments-enabled"
        label="Price this plan by bike segment"
        checked={rule.segmentsEnabled}
        disabled={locked}
        onChange={setEnabled}
      />
      {rule.segmentsEnabled && (
        <>
          <p className="warranty-help">{SEGMENT_MARGIN_HELP}</p>
          {segments.length === 0 && (
            <p className="warranty-help">
              No segments yet.{" "}
              <button
                type="button"
                className="warranty-inline-btn is-primary"
                disabled={locked}
                onClick={() => setSegments(() => starterSegments(models))}
              >
                Start with X Factor and Desire
              </button>
            </p>
          )}
          <div role="radiogroup" aria-label="Default segment">
            {segments.map((segment, index) => {
              const at = (field) => `pricingRule.segments.items.${index}.${field}`;
              const id = (field) => `warranty-segment-${index}-${field}`;
              const title = segment.name.trim() || `Segment ${index + 1}`;
              // The segment name that already holds a model, or null when none other does.
              const ownerOf = (slug) => {
                const owner = segments.find((other, i) => i !== index && other.models.includes(slug));
                return owner ? owner.name.trim() || "another segment" : null;
              };
              const modelMessages = [
                ...msgs(at("models")),
                ...segment.models.flatMap((_, modelIndex) => msgs(at(`models.${modelIndex}`))),
              ];
              return (
                <div className="warranty-subcard" key={index}>
                  <div className="warranty-subcard-head">
                    <strong>{title}</strong>
                    <button
                      type="button"
                      className="warranty-inline-btn is-danger"
                      disabled={locked}
                      aria-label={`Remove segment ${title}`}
                      onClick={() => setSegments((list) => list.filter((_, i) => i !== index))}
                    >
                      Remove
                    </button>
                  </div>
                  <Messages messages={[...msgs(`pricingRule.segments.items.${index}`), ...msgs(at("prices"))]} />
                  <div className="warranty-radios">
                    <Form.Check
                      type="radio"
                      id={id("default")}
                      name="warranty-segment-default"
                      label="Default segment"
                      checked={segment.isDefault}
                      disabled={locked}
                      onChange={() => setDefault(index)}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <TextField
                        id={id("name")}
                        label="Name"
                        value={segment.name}
                        maxLength={60}
                        disabled={locked}
                        messages={msgs(at("name"))}
                        onChange={(value) => setName(index, value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <TextField
                        id={id("key")}
                        label="Key"
                        value={segment.key}
                        maxLength={32}
                        disabled={locked || !segment.isNew}
                        help={segment.isNew ? "Made from the name. Cannot be changed once published." : undefined}
                        messages={msgs(at("key"))}
                        onChange={(value) => patch(index, { key: value, keyEdited: true })}
                      />
                    </div>
                    <div className="col-md-12">
                      <ModelsField
                        id={id("models")}
                        value={segment.models}
                        models={models}
                        loading={modelsLoading}
                        ownerOf={ownerOf}
                        disabled={locked}
                        messages={modelMessages}
                        onChange={(value) => patch(index, { models: value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <NumberField
                        id={id("oneTime")}
                        label="One-time price"
                        kind="rupees"
                        value={segment.oneTime}
                        disabled={locked}
                        messages={msgs(at("prices.oneTimeMinor"))}
                        onChange={(value) => patch(index, { oneTime: value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <NumberField
                        id={id("dealerMargin")}
                        label="Dealer margin"
                        kind="rupees"
                        value={segment.dealerMargin}
                        disabled={locked}
                        messages={msgs(at("dealerMarginMinor"))}
                        onChange={(value) => patch(index, { dealerMargin: value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <NumberField
                        id={id("seriesRank")}
                        label="Coupon series rank"
                        kind="whole"
                        value={segment.seriesRank}
                        disabled={locked}
                        messages={msgs(at("seriesRank"))}
                        onChange={(value) => patch(index, { seriesRank: value })}
                      />
                      <small className="text-muted">
                        A coupon from this segment works on bikes of this rank or higher. Blank: any bike.
                      </small>
                    </div>
                    {tenures.map((tenure) => (
                      <div className="col-md-3" key={`m-${tenure}`}>
                        <NumberField
                          id={id(`monthly-${tenure}`)}
                          label={`${tenure} months instalment`}
                          kind="rupees"
                          value={segment.monthly[tenure] === undefined ? "" : segment.monthly[tenure]}
                          disabled={locked}
                          messages={msgs(at(`prices.monthly.${tenure}`))}
                          onChange={(value) => patchMap(index, "monthly", tenure, value)}
                        />
                      </div>
                    ))}
                    {upfront.map((benefit) => (
                      <div className="col-md-3" key={`a-${benefit.key}`}>
                        <NumberField
                          id={id(`addon-${benefit.key}`)}
                          label={`${benefit.label || benefit.key} price`}
                          kind="rupees"
                          value={segment.addons[benefit.key] === undefined ? "" : segment.addons[benefit.key]}
                          disabled={locked}
                          messages={msgs(at(`prices.addons.${benefit.key}`))}
                          onChange={(value) => patchMap(index, "addons", benefit.key, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className="warranty-inline-btn is-primary"
            disabled={locked}
            onClick={() => setSegments((list) => [...list, emptySegment(list.length === 0)])}
          >
            Add segment
          </button>
        </>
      )}
    </Section>
  );
};

export default SegmentsSection;
